import { ReactNode, useState, useEffect, createContext, useContext, useCallback } from 'react'
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@mui/material'
import {
  AccountCircle,
  Search,
  Menu as MenuIcon,
  Logout,
  FilterAlt,
  Check,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { useTranslation } from '@hooks/useTranslation'
import { Sidebar, SIDEBAR_WIDTH_FULL, SIDEBAR_WIDTH_ICONS, type SidebarCollapseMode } from '@components/navigation/Sidebar'

// Available languages with flags
const LANGUAGES = [
  { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
  { code: 'es-ES', name: 'Español', flag: '🇪🇸' },
  { code: 'fr-FR', name: 'Français', flag: '🇫🇷' },
  { code: 'de-DE', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pl-PL', name: 'Polski', flag: '🇵🇱' },
]

const SIDEBAR_COLLAPSE_KEY = 'sidebarCollapseMode'

// Build version timestamp - format: yyyyMMddhhmm
const BUILD_VERSION = '202510280147'

// Context for sidebar width
interface SidebarContextType {
  sidebarWidth: number
}

const SidebarContext = createContext<SidebarContextType>({ sidebarWidth: SIDEBAR_WIDTH_FULL })

export const useSidebarWidth = () => useContext(SidebarContext)

// Context for page title
interface PageTitleContextType {
  pageTitle: string
  setPageTitle: (title: string) => void
}

const PageTitleContext = createContext<PageTitleContextType>({
  pageTitle: 'Dashboard',
  setPageTitle: () => {},
})

export const usePageTitle = (title?: string) => {
  const { pageTitle, setPageTitle } = useContext(PageTitleContext)

  useEffect(() => {
    if (title) {
      setPageTitle(title)
    }
  }, [title, setPageTitle])

  return { pageTitle, setPageTitle }
}

interface MainLayoutProps {
  children: ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  const { logout } = useAuthStore()
  const { language, changeLanguage } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDialogOpen, setSearchDialogOpen] = useState(false)
  const [pageTitle, setPageTitleState] = useState('Dashboard')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) // <900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')) // <600px

  // Memoized setPageTitle to prevent unnecessary re-renders
  const setPageTitle = useCallback((title: string) => {
    setPageTitleState(title)
  }, [])

  // Update browser tab title when pageTitle changes
  useEffect(() => {
    document.title = `HiddenAdv - ${pageTitle}`
  }, [pageTitle])

  // Sidebar collapse state with localStorage persistence
  const [sidebarCollapseMode, setSidebarCollapseMode] = useState<SidebarCollapseMode>(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSE_KEY)
    return (saved ? parseInt(saved, 10) : 2) as SidebarCollapseMode
  })

  const handleSidebarCollapseModeChange = (mode: SidebarCollapseMode) => {
    setSidebarCollapseMode(mode)
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, mode.toString())

    // Dispatch resize events during and after transition to trigger map resize
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 250)
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 400)
  }

  // Get current sidebar width based on collapse mode
  const currentSidebarWidth = sidebarCollapseMode === 0 ? 0 : sidebarCollapseMode === 1 ? SIDEBAR_WIDTH_ICONS : SIDEBAR_WIDTH_FULL

  // Clear search query when navigating away from search results page or to advanced search
  useEffect(() => {
    if (!location.pathname.startsWith('/search') || location.pathname === '/search/advanced') {
      setSearchQuery('')
    }
  }, [location.pathname])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget)
  }

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null)
  }

  const handleLogout = () => {
    handleAccountMenuClose()
    logout()
  }

  const handleLanguageChange = async (languageCode: string) => {
    await changeLanguage(languageCode)
    handleAccountMenuClose()
  }

  // Debounced search handler
  useEffect(() => {
    if (!searchQuery.trim()) {
      return
    }

    const timer = setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, navigate])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
      setSearchDialogOpen(false)
      setSearchQuery('')
    }
  }

  const handleSearchIconClick = () => {
    if (isSmallScreen) {
      setSearchDialogOpen(true)
    } else {
      navigate('/search/advanced')
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', width: '100%' }}>
      {/* Header spans full width */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'primary.main',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexShrink: 0,
        }}
      >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Left: Hamburger Menu + Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Hamburger menu - show on mobile or when sidebar is hidden on desktop */}
              <IconButton
                color="inherit"
                edge="start"
                onClick={isMobile ? handleDrawerToggle : () => handleSidebarCollapseModeChange(2)}
                sx={{
                  display: {
                    xs: 'inline-flex',
                    md: sidebarCollapseMode === 0 ? 'inline-flex' : 'none',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>

              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {pageTitle}
              </Typography>
            </Box>

            {/* Right: Search Bar + User Actions */}
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              {/* Search Bar - show on larger screens */}
              <TextField
                  placeholder="Search adventures, users, groups..."
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  onBlur={() => setSearchQuery('')}
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    flex: 1,
                    maxWidth: { sm: 300, md: 450, lg: 600 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: 1,
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'white',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '8.5px 14px',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        opacity: 1,
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => navigate('/search/advanced')}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                              color: 'white',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          <FilterAlt fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

              {/* User Actions */}
              {/* Search Icon - show on small screens, hide on larger screens where search bar is visible */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleSearchIconClick}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <Search />
              </IconButton>
              <IconButton color="inherit" size="small" onClick={handleAccountMenuOpen}>
                <AccountCircle />
              </IconButton>
            </Box>
          </Toolbar>
      </AppBar>

      {/* Account Menu */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        onClick={handleAccountMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 200,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={lang.code === language}
          >
            <ListItemText>
              <span style={{ marginRight: 8 }}>{lang.flag}</span>
              {lang.name}
            </ListItemText>
            {lang.code === language && (
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <Check fontSize="small" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Content area with sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', width: '100%' }}>
        <Sidebar
          mobileOpen={mobileOpen}
          onDrawerToggle={handleDrawerToggle}
          isMobile={isMobile}
          collapseMode={sidebarCollapseMode}
          onCollapseModeChange={handleSidebarCollapseModeChange}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            flex: 1,
            minWidth: 0,
            backgroundColor: 'background.default',
            overflow: 'auto',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <PageTitleContext.Provider value={{ pageTitle, setPageTitle }}>
            <SidebarContext.Provider value={{ sidebarWidth: currentSidebarWidth }}>
              {children}
            </SidebarContext.Provider>
          </PageTitleContext.Provider>
        </Box>
      </Box>

      {/* Mobile Search Dialog */}
      <Dialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search Adventures</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search adventures, users, groups..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                navigate('/search/advanced')
                setSearchDialogOpen(false)
              }}
            >
              Advanced Search
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
